// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleUtils } from '@article/util/article-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { Vector3 } from 'three';
import { Dictionary } from 'typescript-collections';
import { WireBridgeService } from '../manager/wire-bridge.service';

@Injectable({ providedIn: 'root' })
export class InsertArticleUtils {
  constructor(
    private containerService: ContainerService,
    private articleContainerUtils: ArticleContainerUtils,
    private articleConnectionService: ArticleConnectionService,
    private wireBridgeService: WireBridgeService,
  ) {}

  public moveArticles(sortedArticles: ArticleContainer[], moveMap: Dictionary<string, Vector3>, negateMoveOffset: boolean = false): void {
    const processedArticlesGUIDs: string[] = []; // articles that already have been moved
    for (const sortedArticle of sortedArticles) {
      // if article has not been moved yet
      if (processedArticlesGUIDs.indexOf(sortedArticle.articleGUID) > -1) {
        continue;
      }
      if (!moveMap.containsKey(sortedArticle.articleGUID)) {
        continue;
      }
      const offset: Vector3 = moveMap.getValue(sortedArticle.articleGUID);
      this.moveSingleArticleByOffset(sortedArticle, offset, negateMoveOffset);
      const childArticles: ArticleContainer[] = this.articleContainerUtils.getConnectedArticlesRecursively(sortedArticle);
      for (const childArticle of childArticles) {
        if (processedArticlesGUIDs.indexOf(childArticle.articleGUID) === -1) {
          this.moveSingleArticleByOffset(childArticle, offset, negateMoveOffset);
          processedArticlesGUIDs.push(childArticle.articleGUID);
        }
      }
      this.wireBridgeService.updateWireBridgeObjects(childArticles);
    }
  }

  public moveSingleArticleByOffset(articleContainer: ArticleContainer, offset: Vector3, negateMoveOffset: boolean): void {
    if (!offset) {
      return;
    }
    const delta: Vector3 = offset.clone();
    if (negateMoveOffset) {
      delta.negate();
    }
    articleContainer.moveArticle(delta);
  }

  public moveArticlesByMap(moveArticlesMap: Dictionary<string, Vector3>, negateMoveOffset: boolean = false): void {
    if (!moveArticlesMap) {
      return;
    }
    const articleGUIDs: string[] = [];
    for (const guid of moveArticlesMap.keys()) {
      articleGUIDs.push(guid);
    }
    this.moveArticles(this.containerService.findArticleContainersInGlobalByGuids(articleGUIDs), moveArticlesMap, negateMoveOffset);
  }

  public static mergeMoveMap(targetMap: Dictionary<string, Vector3>, sourceMap: Dictionary<string, Vector3>): void {
    if (!targetMap || !sourceMap) {
      return;
    }
    for (const articleGUID of sourceMap.keys()) {
      this.addToMoveMap(targetMap, articleGUID, sourceMap.getValue(articleGUID));
    }
  }

  public static addToMoveMap(targetMap: Dictionary<string, Vector3>, key: string, value: Vector3): void {
    if (targetMap.containsKey(key)) {
      const oldOffsetValue: Vector3 = targetMap.getValue(key);
      targetMap.setValue(key, oldOffsetValue.clone().add(value));
    } else {
      targetMap.setValue(key, value);
    }
  }


  public getBreakCombinedArticles(moveMap: Dictionary<string, Vector3>): ArticleContainer[] {
    if (!moveMap) {
      return [];
    }

    const unmovedArticles: ArticleContainer[] = [];
    for (const articleGUID of moveMap.keys()) {
      const movedArticle: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleGUID);
      const combinedArticles: ArticleContainer[] = this.articleContainerUtils.findCombinedArticles(movedArticle);
      for (const article of combinedArticles) {
        // if paste in middle of labeling
        if (!moveMap.getValue(article.articleGUID) && unmovedArticles.indexOf(article) === -1) {
          unmovedArticles.push(article);
        }
      }
    }
    return unmovedArticles;
  }

  public findChildArticleContainers(
    parentContainers: ArticleContainer[],
    isOnlyLabelingArticleContainer: boolean = false,
    searchRecursively: boolean = true
  ): ArticleContainer[] {
    const childContainers: ArticleContainer[] = [];
    for (const parentContainer of parentContainers) {
      const connectedContainers: ArticleContainer[] = searchRecursively
        ? this.articleContainerUtils.getConnectedArticlesRecursively(parentContainer)
        : this.containerService.getConnectedArticleContainers(parentContainer.articleUsage);
      for (const childContainer of connectedContainers) {
        if (!this.articleContainerUtils.findArticleContainerByGUID(childContainer.articleGUID, childContainers)) {
          if (!isOnlyLabelingArticleContainer || ArticleUtils.isDynamicLabelingArticle(childContainer.article)) {
            childContainers.push(childContainer);
          }
        }
      }
    }
    return childContainers;
  }

  public isRotationArticlesOnRailAvailable(articleContainers: ArticleContainer[], insertArticleHelperFactory: InsertArticleHelperFactory): boolean {
    if (ArrayUtils.isEmpty(articleContainers)) {
      return false;
    }
    if (!this.articleConnectionService.isArticleConnectedToRangeOnLineArticleWithConfigured(articleContainers[0])) {
      return true;
    }
    const insertHelper: InsertArticleHelper = insertArticleHelperFactory.createInstanceByConnectedArticleContainer(articleContainers[0]);
    if (!insertHelper) {
      return true;
    }
    const commonLine: AdvancedLine = insertHelper.calculateCommonArticlesLine(articleContainers);
    if (!commonLine || commonLine.overlapLeft === commonLine.overlapRight) {
      return true;
    }
    if (commonLine.overlapLeft > commonLine.overlapRight || commonLine.overlapLeft === 0) {
      const leftArticle: ArticleContainer = insertHelper.getLeftArticleContainerByPoint(commonLine.start);
      const leftArticleLine: AdvancedLine = insertHelper.getArticleLine(leftArticle);
      if (leftArticleLine && commonLine.hasLine(leftArticleLine, false)) {
        const overlapDistance: number = commonLine.start.distanceTo(leftArticleLine.end);
        if (overlapDistance > 0 && !ArrayUtils.sourceArrayContainsElementFromTargetArray(commonLine.overlapLeftTypes, commonLine.overlapRightTypes)) {
          return false;
        }
        if (commonLine.overlapRight < overlapDistance && commonLine.overlapRight !== 0) {
          return false;
        }
      }
    }
    if (commonLine.overlapRight > commonLine.overlapLeft || commonLine.overlapRight === 0) {
      const rightArticle: ArticleContainer = insertHelper.getRightArticleContainerByPoint(commonLine.end);
      const rightArticleLine: AdvancedLine = insertHelper.getArticleLine(rightArticle);
      if (rightArticleLine && commonLine.hasLine(rightArticleLine, false)) {
        const overlapDistance: number = commonLine.end.distanceTo(rightArticleLine.start);
        if (overlapDistance > 0 && !ArrayUtils.sourceArrayContainsElementFromTargetArray(commonLine.overlapLeftTypes, commonLine.overlapRightTypes)) {
          return false;
        }
        if (commonLine.overlapLeft < overlapDistance && commonLine.overlapLeft !== 0) {
          return false;
        }
      }
    }

    return true;
  }
}
